// src/features/activity/FeedTab.tsx
// Browse everything that happened in a project/team/tenant, not just what's
// unread — Inbox intentionally drops items once acknowledged. ListActivities.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { Input } from '@react/components';
import { Select } from '@/components/Select';
import { ListActivities } from '@core/api/endpoints/activity/list_activities';
import type { Activity } from '@core/types/activity';
import { CATEGORY_VALUES, categoryKey, relativeTime, errMessage } from './logic';

interface FeedTabProps {
  onOpenActivity: (activity: Activity) => void;
  selectedId?: string;
}

type ScopeChoice = 'ACTIVITY_SCOPE_PROJECT' | 'ACTIVITY_SCOPE_TEAM' | 'ACTIVITY_SCOPE_TENANT';

export const FeedTab = observer(({ onOpenActivity, selectedId }: FeedTabProps) => {
  const { t } = useI18n();
  const [scope, setScope] = useState<ScopeChoice>('ACTIVITY_SCOPE_PROJECT');
  const [scopeId, setScopeId] = useState('');
  const [category, setCategory] = useState<string>('');
  const [items, setItems] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    if (!scopeId.trim()) return;
    setLoading(true);
    setError(null);
    ListActivities({ scope, scope_id: scopeId.trim(), category: category || undefined, limit: 50 })
      .then((res) => { setItems(res.activities ?? []); setLoaded(true); })
      .catch((e: unknown) => setError(errMessage(e, t('activity.feed.loadError'))))
      .finally(() => setLoading(false));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('activity.feed.subtitle')}</p>

      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t('activity.feed.scope.label')}
          value={scope}
          onChange={(e) => setScope(e.target.value as ScopeChoice)}
          className="w-40"
          options={[
            { value: 'ACTIVITY_SCOPE_PROJECT', label: t('activity.feed.scope.project') },
            { value: 'ACTIVITY_SCOPE_TEAM', label: t('activity.feed.scope.team') },
            { value: 'ACTIVITY_SCOPE_TENANT', label: t('activity.feed.scope.tenant') },
          ]}
        />
        <Input
          label={t('activity.feed.scopeId')}
          value={scopeId}
          onChange={(e) => setScopeId(e.target.value)}
          className="w-56"
          fullWidth={false}
        />
        <Select
          label={t('activity.feed.category')}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-40"
          options={[{ value: '', label: t('common.all') }, ...CATEGORY_VALUES.map((c) => ({ value: c, label: t(`activity.category.${categoryKey(c)}`) }))]}
        />
        <Button size="sm" onClick={load} isLoading={loading} disabled={!scopeId.trim()}>
          {t('activity.feed.load')}
        </Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {loaded && !loading && items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('activity.feed.empty')}</p>
        </div>
      )}

      <ul className="divide-y divide-border rounded-md border border-border">
        {items.map((a) => {
          const id = a.entity?.id ?? '';
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => onOpenActivity(a)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted ${selectedId === id ? 'bg-muted/50' : ''}`}
              >
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">{a.summary}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{relativeTime(a.created_at as unknown as string)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

export default FeedTab;
